import { RequestInterval, TargetTagType } from '@/common/utils';
import type { UserTagRequest } from '@/api-client/data/model/user-tag-request';
import type { RequestType } from '@/dashboard/features/widgetsAPI/domain/apiRequest.types';
import { WidgetTarget } from '@/dashboard/shared/types/widgets.types';

export type ArgParam = Record<
  string,
  { value: string; tags?: boolean; userTags?: UserTagRequest[]; targetIds?: string }
>;

export type OverrideParam = {
  overrideParam?: {
    fromTime: string;
    toTime: string;
    interval?: RequestInterval | 'I1s';
  };
};

export const encodeArgParam = (
  value: string | boolean | number | (string | number)[] | null | undefined,
): string | undefined => {
  if (value == null) return undefined;
  switch (typeof value) {
    case 'string':
      return `'${value}'`;
    case 'number':
    case 'boolean':
      return `${value}`;
    case 'object':
      if (Array.isArray(value)) {
        return value.map(encodeArgParam).join(', ');
      }
      break;
    default:
      return `${value}`;
  }
  return `${value}`;
};

export const decodeArgParam = (value: string | undefined): string | undefined => {
  if (value == null) return undefined;
  return value.replaceAll("'", '').trim();
};

export const encodeGlobalVariableArgParam = (
  tags: {
    tagKey: WidgetTarget['tagKey'];
    tagType: TargetTagType | 'business_service' | 'business_service_group';
    tagValue: string;
  }[],
): string => {
  return tags
    .map(({ tagKey, tagType, tagValue }) => {
      return `${tagKey};${tagType};${tagValue}`;
    })
    .join(',');
};

export const decodeGlobalVariableArgParam = (
  value: string | undefined,
): { tagKey: string; tagType: string; tagValue: string }[] | undefined => {
  if (value == null) return undefined;

  const argParams = value.split(',');
  return argParams.map((argParam) => {
    const [tagKey, tagType, tagValue] = argParam.split(';');
    return { tagKey, tagType, tagValue };
  });
};
/**
 * 커스텀 지표의 targetIds를 추출합니다.
 * @param argParam
 * @param targetIds 범례 위젯에서 전체가 아닌 일부를 선택한 경우의 선택한 값이 들어있습니다.
 * @returns targetIds
 */
export const getTargetIds = (argParam: ArgParam, targetIds?: string[]): string[] | undefined => {
  const isGlobalVariableTargetId = argParam?.targetIds?.tags === true;
  const argParamTags = argParam?.targetIds?.value;
  return isGlobalVariableTargetId
    ? targetIds
    : (argParamTags
        ?.split(',')
        .map((id) => decodeArgParam(id))
        .filter((id) => !!id) as string[] | undefined);
};

// NOTE: request에서 tags가 있는 경우, targetIds property는 무시됩니다.
// TopologyV7Request는 argParam이 없으므로 제외
export type RequestTypeWithArgParam = Exclude<RequestType, 'TopologyV7Request'>;

export type RequestParamWithArgParam<T extends RequestTypeWithArgParam> = T & {
  id: string;
  argParam?: ArgParam[];
  dataId: string;
  targetIds?: string[];
} & OverrideParam;
